/* eslint-disable @typescript-eslint/no-explicit-any */
import DeleteIcon from "@mui/icons-material/Delete";
import { Autocomplete, IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import React from "react";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import MDInput from "@/components/atoms/MDInput";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { PAYMENTS_METHOD } from "@/constants/payments.constants";
import { convertCurrencyToNumber } from "@/helpers/currency-helpers";
import { IOrderPayment } from "@/interfaces/order.interfaces";
import { PaymentDataTabMessages } from "@/messages/order-form-page/payment-data-tab.messages";
import { SelectData } from "@/types/form.types";
import { PaymentMethod } from "@/types/payment.types";

interface Props {
  payment: IOrderPayment;
  index: number;
  messages: PaymentDataTabMessages;
  isTalk2BuyChannel: boolean;
}

export function PaymentTableRow({ payment, index, messages }: Props) {
  const paymentMethodTypeOptions: SelectData<PaymentMethod>[] = PAYMENTS_METHOD(messages);
  const { values, setFieldValue, errors } = useFormikContext<OrderFormData>();
  const { payments } = values;

  const handlePaymentAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = convertCurrencyToNumber(event.target.value);
    setFieldValue(event.target.name, numericValue);
  };

  const handlePaymentMethodChange = (value: PaymentMethod | undefined) => {
    setFieldValue(`payments[${index}].paymentMethodType`, value ?? "");
  };

  const handleRemovePayment = (selectedPayment: IOrderPayment) => {
    if (selectedPayment.id) {
      setFieldValue("removedPayments", [...values.removedPayments, selectedPayment]);
      const newPayments = payments.filter((payment) => payment.id !== selectedPayment.id);
      setFieldValue("payments", newPayments);
    } else {
      const newPayments = payments.filter(
        (payment) => payment.rowDate !== String(selectedPayment.rowDate)
      );
      setFieldValue("payments", newPayments);
    }
  };

  return (
    <TableRow index={index}>
      <TableCell>
        <DatePicker
          value={dayjs(payment.paymentDate)}
          disabled={values.disabledButton}
          onChange={(value) =>
            setFieldValue(`payments[${index}].paymentDate`, value ? value.toISOString() : "")
          }
          slotProps={{
            textField: {
              helperText: errors.paymentDate,
              error: !!errors.paymentDate,
              variant: "standard",
              fullWidth: true,
              InputLabelProps: { shrink: true },
            },
          }}
        />
      </TableCell>

      <TableCell>
        <Autocomplete
          fullWidth
          options={paymentMethodTypeOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          value={
            paymentMethodTypeOptions.find((option) => option.value === payment.paymentMethodType) ||
            null
          }
          onChange={(_, newValue) => handlePaymentMethodChange(newValue?.value)}
          disabled={values.disabledButton}
          renderInput={(params) => (
            <MDInput
              {...params}
              name={`payments[${index}].paymentMethodType`}
              variant="standard"
              error={!!errors.paymentMethodType}
              helperText={errors.paymentMethodType}
            />
          )}
        />
      </TableCell>

      <TableCell>
        <CurrencyInput
          name={`payments[${index}].paymentAmount`}
          value={payment.paymentAmount}
          variant="standard"
          onChange={handlePaymentAmountChange}
          fullWidth
          error={!!errors.payments}
          helperText={errors.payments?.toString()}
          disabled={values.disabledButton}
        />
      </TableCell>

      <TableCell>
        <IconButton
          onClick={() => handleRemovePayment(payment)}
          disabled={values.disabledButton}
          aria-label="delete payment"
        >
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
